import type { ManagerDetail, StrategyVersion , FirstStepStrategyVal} from '@/types'
import { colors, groupColors } from '@/pages/static'
import type { CSSProperties } from 'react'
import { useEffect, useState } from 'react'
import { Button, Input, Spin, Tag } from 'antd'
import './index.scss'
import { marketOptions } from '@/pages/static'

type FirstStepProps = {
    loading: boolean
    managers: ManagerDetail[]
    strategyList: StrategyVersion[]
    value: FirstStepStrategyVal
    onSelectManager: (managerId: number) => void
    onChange: (value: FirstStepStrategyVal) => void
    noVersionsTip?: string
}

function getTagClassName(type: string) {
    if (type === 'HK') {
        return 'is-purple'
    }

    return 'is-success'
}

export default function FirstStep({ loading, managers, strategyList, value, onSelectManager, onChange, noVersionsTip }: FirstStepProps) {
    const [submitVal, setSubmitVal] = useState<FirstStepStrategyVal>({
        manager_id: 0,
        stock_pool_id: 0,
        stock_pool_name: '',
        strategy_version: {} as StrategyVersion,
    })

    const [isShowStrategyDesc, setIsShowStrategyDesc] = useState(false)

    const [strategy_snapshot, setStrategy_snapshot] = useState(submitVal.strategy_version.strategy_snapshot)

    useEffect(() => {
        setSubmitVal(value)
        setStrategy_snapshot(value.strategy_version.strategy_snapshot)
    }, [value.manager_id, value.strategy_version])

    const handleSelectManager = (manager: ManagerDetail) => {
        const nextValue = {
            manager_id: manager.id ?? 0,
            stock_pool_id: manager.default_stock_pool_id ?? 0,
            stock_pool_name: manager.default_stock_pool_name ?? '',
            strategy_version: { strategy_snapshot: manager.strategy ?? '' } as StrategyVersion,
        }

        setSubmitVal(nextValue)
        onChange(nextValue)
        onSelectManager(manager.id ?? 0)
    }

    const selectVersion =(version: StrategyVersion) => {
        const nextValue = {
            ...submitVal,
            strategy_version: version,
        }

        setSubmitVal(nextValue)
        setStrategy_snapshot(version.strategy_snapshot)
        onChange(nextValue)
    }

    const saveAdjustStrategyDesc = () => {
        setIsShowStrategyDesc(false)
        setSubmitVal({ ...submitVal, strategy_version: { ...submitVal.strategy_version, strategy_snapshot: strategy_snapshot } })
        onChange({ ...submitVal, strategy_version: { ...submitVal.strategy_version, strategy_snapshot: strategy_snapshot } })
    }

    const cancelAdjustStrategyDesc = (isShowStrategyDesc: boolean) => {
        setIsShowStrategyDesc(isShowStrategyDesc)
        setStrategy_snapshot(submitVal.strategy_version.strategy_snapshot)
    }

    return (
        <section className="first-step">
            <div className='first-step__section'>
                <div className="first-step__panel-title text-12 color-2 font-weight-600">选择组合经理</div>
                {
                    managers.length === 0 ? 
                    <div 
                        style={{
                            padding: '50px 0',
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            height: '100%' 
                        }}
                    >
                            <Spin />
                    </div> : 
                    <div className="first-step__manager-list">
                        {
                             managers.map((manager, index) => (
                                <div
                                    key={manager.id}
                                    className={`first-step__manager-card ${submitVal.manager_id === manager.id ? 'is-selected' : ''}`}
                                    style={{ '--manager-color': colors[index % colors.length], '--group-color': groupColors[index % colors.length] } as CSSProperties}
                                    onClick={() => handleSelectManager(manager)}
                                >
                                    <div className="first-step__manager-avatar font-weight-700">{manager.initials}</div>
                                    <div className="first-step__manager-main">
                                        <div className="first-step__manager-name font-weight-700">{manager.nickname}</div>
                                        <div className="first-step__manager-meta">
                                            {manager.style} · {manager.risk_level}
                                        </div>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                }
            </div>

            {
                submitVal.manager_id !==0 && (
                    <>
                        <div className='first-step__section'>
                            <div className="first-step__panel-title text-12 color-2 font-weight-600">
                                选择策略版本 
                                {/* <span className='text-11 color-3' style={{ marginLeft: 8 }}>已激活/已归档版本均可用于回测</span> */}
                            </div>
                            {
                                loading && 
                                <div style={{ 
                                    padding: '50px 0', 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    alignItems: 'center', 
                                    height: '100%' }}>
                                        <Spin />
                                </div> 
                            }
                            {
                                strategyList.length > 0 ? strategyList.map((version) => (
                                    <article
                                        key={version.id}
                                        className={`strategy-version__card flex items-center ${submitVal.strategy_version.id === version.id ? 'is-selected-version' : ''}`}
                                        onClick={() => selectVersion(version)}
                                    >
                                        <div className="strategy-version__version font-weight-700">{version.version_no}</div>
                                        <div className="strategy-version__card-main grid gap-4">
                                            <div className="strategy-version__card-top">
                                                <div className="strategy-version__card-copy">
                                                    <div className="strategy-version__card-title font-weight-700">{version.name}</div>
                                                    <div className="strategy-version__card-desc">{version.strategy_snapshot}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="strategy-version__card-tags">
                                            <Tag className={`is-round ${version.status === 'active' ? 'is-success' : version.status === 'archived' || version.status === 'draft' ? '' : 'is-warning'} text-10`} style={{ padding: '1px 8px' ,marginRight: 8}}>
                                                {(version.status === 'active' ? '已激活' : version.status === 'archived' ? '已归档' : version.status === 'draft' ? '草稿' : '待审批')}
                                            </Tag>
                                            <Tag className={`${getTagClassName(version.market)} text-10`}>
                                                {marketOptions[version.market as keyof typeof marketOptions]}
                                            </Tag>
                                        </div>
                                    </article>
                                ))
                                    :
                                    (
                                        <div className="first-step__empty-tip text-11 color-3">
                                            {noVersionsTip ?? '该基金经理暂无可用版本（需先激活至少一个版本才可发起回测）。'}
                                        </div>
                                    )
                            }
                        </div>
                        {
                            submitVal.strategy_version.id && (
                                <div className="first-step__section">
                                    <div className="first-step__section-version">
                                        <div className="first-step__version-title flex flex-between font-weight-600">
                                            <div className="first-step__version-title-left">
                                                <span className="text-12 font-weight-600" style={{ color: 'var(--accent)',marginRight: 8 }}>{submitVal.strategy_version.version_no}</span>
                                                <span className="text-14 color-2">{submitVal.strategy_version.name}</span>
                                            </div>
                                            <span className={`text-11 ${!isShowStrategyDesc ? 'is-open' : ''}`} style={{ cursor: 'pointer',color: 'var(--accent)'}} onClick={() => setIsShowStrategyDesc(!isShowStrategyDesc)}>{isShowStrategyDesc ? '编辑策略描述' : '手动调整'}</span>
                                        </div>
                                        {!isShowStrategyDesc ? <div className='first-step__version-desc'>
                                                <Input.TextArea
                                                    rows={15}
                                                    readOnly
                                                    className='ant-transparent-input'
                                                    style={{ marginBottom: 12 }}
                                                    value={submitVal.strategy_version.strategy_snapshot}
                                                />
                                            <div className="first-step__version-change-tip text-11 color-3">
                                                <div className="first-step__version-constraints">
                                                    {submitVal.strategy_version.constraints.map((constraint) => (
                                                        <Tag key={constraint} className="border-tag text-11 is-round-tag version-constraint-tag" style={{ marginRight: 8 }}>
                                                            {constraint}
                                                        </Tag>
                                                    ))}
                                                </div>
                                               <span className="first-step__version-change-tip-desc"> &nbsp;&nbsp;可通过右侧 AI 助手优化策略，或点击「手动调整」直接编辑参数</span>
                                            </div>
                                        </div> : (
                                            <div className="first-step__version-desc-input" style={{ padding: '12px 16px' }}>
                                                <Input.TextArea
                                                    rows={15}
                                                    value={strategy_snapshot}
                                                    onChange={(e) => setStrategy_snapshot(e.target.value)}
                                                />
                                                <Button type="primary" size="small" style={{ marginTop: 12, marginRight: 12 }} onClick={() => saveAdjustStrategyDesc()}>保存调整</Button>
                                                <Button size="small" style={{ marginTop: 12 }} onClick={() => cancelAdjustStrategyDesc(false)}>取消</Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        }
                    </>
                )
            }
        </section>
    )
}